import React, { useState, useEffect } from "react";
import { MdSettings } from "react-icons/md";
import { Dialog } from "components/dialog";

interface EditLLMModelModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  onSubmit: (data: LLMModelData) => void;
  initialData: LLMModelData;
  settingsType: "global" | "organization";
}

interface LLMModelData {
  name: string;
  type: string;
  configuration: string;
}

const EditLLMModelModal: React.FC<EditLLMModelModalProps> = ({
  isOpen,
  setIsOpen,
  onSubmit,
  initialData,
  settingsType,
}) => {
  const [formData, setFormData] = useState<LLMModelData>({
    name: "",
    type: "",
    configuration: "",
  });

  const titlePrefix = settingsType === "global" ? "Global" : "Organization";

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    setIsOpen(false);
  };

  return (
    <Dialog
      header={`Edit ${titlePrefix} LLM model`}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      className="max-w-lg w-full"
    >
      <form onSubmit={handleSubmit} className="flex max-h-[70vh] flex-col gap-6 p-2">
        <div className="space-y-6">
          {/* Model Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              LLM model name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter LLM model name"
              required
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:focus:border-blue-400"
            />
          </div>

          {/* Model Type - Display Only */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              LLM model type
            </label>
            <div className="relative mt-1">
              <input
                type="text"
                value={formData.type}
                disabled
                className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-gray-100 cursor-not-allowed dark:bg-gray-700 dark:text-white"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Model type cannot be changed when editing
            </p>
          </div>

          {/* Configuration */}
          <div>
            <div className="mb-2 flex items-center gap-2">
              <MdSettings className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                LLM model configuration
              </label>
            </div>
            <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
              Configuration (Json type)
            </p>
            <textarea
              value={formData.configuration}
              onChange={(e) => setFormData(prev => ({ ...prev, configuration: e.target.value }))}
              placeholder="Enter json type configuration"
              rows={8}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:focus:border-blue-400"
            />
          </div>

        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-3 pt-6">
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
          >
            Update Model
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default EditLLMModelModal;

